import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Medal, Crown, TrendingUp, RefreshCw, History, Percent } from 'lucide-react';
import { useAuth } from '../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ['super_admin', 'admin', 'office_admin', 'project_manager_estimator'];

const money = (n) => `$${Math.round(n || 0).toLocaleString('en-CA')}`;
const compact = (n) => (n >= 1e6 ? `$${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `$${Math.round(n / 1e3)}K` : money(n));
const initials = (name) => name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
const pct = (n) => `${Math.round(n)}%`;

const marginTone = (m, s) => {
  if (!m || m.pct == null) return 'bg-slate-100 text-slate-500 border-slate-200';
  if (m.pct >= (s?.margin_target_pct ?? 35)) return 'bg-emerald-100 text-emerald-900 border-emerald-300';
  if (m.pct >= (s?.margin_warn_pct ?? 20)) return 'bg-amber-100 text-amber-900 border-amber-300';
  return 'bg-red-100 text-red-900 border-red-300';
};

// "avg margin 31%" pill (estimate from hours + POs + materials logged in Ops); click for the job breakdown.
const MarginPill = ({ e, settings, open, onToggle, small }) => {
  const m = e.margin;
  if (!m) return null;
  const none = m.pct == null;
  return (
    <button type="button" onClick={onToggle} data-testid={`leaderboard-margin-${e.pm.replace(/\s+/g, '-').toLowerCase()}`}
      title={none ? 'No invoiced job has hours logged yet' : `${m.jobs_counted} of ${m.jobs_invoiced} invoiced jobs have hours logged · ${m.labour_hours} h · click for the jobs`}
      className={`inline-flex items-center gap-1 rounded-full border font-bold ${small ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'} ${marginTone(m, settings)} ${open ? 'ring-2 ring-slate-400' : ''}`}>
      <Percent className={small ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      {none ? 'margin —' : `avg margin ${pct(m.pct)}`}
      {!none && !small && <span className="font-medium opacity-70">· {m.jobs_counted}/{m.jobs_invoiced} jobs</span>}
    </button>
  );
};

const MarginDetail = ({ e }) => {
  const m = e.margin;
  if (!m || m.pct == null) {
    return <p className="mt-2 text-[11px] text-slate-500" data-testid="leaderboard-margin-empty">No invoiced job has crew hours logged in Ops yet — margin fills in as time gets clocked to the WOs.</p>;
  }
  const Job = ({ j }) => (
    <span className="flex items-center justify-between gap-2 text-[11px]">
      <span className="truncate text-slate-700"><b className="font-mono">{j.wo}</b> {j.customer}</span>
      <span className={`font-mono font-bold shrink-0 ${j.margin_pct < 0 ? 'text-red-700' : 'text-slate-900'}`}>{pct(j.margin_pct)}</span>
    </span>
  );
  return (
    <div className="mt-2 rounded-xl bg-white/80 border border-slate-200 p-2.5 space-y-2 text-slate-700" data-testid="leaderboard-margin-detail">
      <p className="text-[11px]">
        <b>{m.jobs_counted}</b> of {m.jobs_invoiced} invoiced jobs have hours logged · {money(m.revenue_counted)} billed vs {money(m.cost_counted)} cost
        <span className="block text-slate-500">labour {money(m.labour_cost)} ({m.labour_hours} h) · POs {money(m.po_cost)} · materials {money(m.materials_cost)}{m.qb_jobs ? ` · ${m.qb_jobs} from QuickBooks hours` : ''}</span>
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
        <div><p className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">Best</p>{m.best.map(j => <Job key={j.wo} j={j} />)}</div>
        <div><p className="text-[10px] uppercase font-bold tracking-wider text-red-700">Needs a look</p>{m.worst.map(j => <Job key={j.wo} j={j} />)}</div>
      </div>
      {m.rate_missing?.length > 0 && <p className="text-[10px] text-amber-700">Default wage used for {m.rate_missing.slice(0, 4).join(', ')}{m.rate_missing.length > 4 ? ` +${m.rate_missing.length - 4}` : ''} — set their rate in Admin → Pay Rates.</p>}
    </div>
  );
};

const MEDAL = {
  gold: { label: 'GOLD', place: '1st', card: 'from-amber-200 via-yellow-100 to-amber-50 border-amber-400 shadow-amber-300/50', ring: 'bg-amber-400 text-amber-950 ring-amber-200', text: 'text-amber-800', bar: 'bg-amber-400', Icon: Crown },
  silver: { label: 'SILVER', place: '2nd', card: 'from-slate-300 via-slate-100 to-slate-50 border-slate-400 shadow-slate-300/50', ring: 'bg-slate-400 text-white ring-slate-200', text: 'text-slate-700', bar: 'bg-slate-400', Icon: Medal },
  bronze: { label: 'BRONZE', place: '3rd', card: 'from-orange-200 via-orange-100 to-orange-50 border-orange-400 shadow-orange-300/50', ring: 'bg-orange-500 text-white ring-orange-200', text: 'text-orange-800', bar: 'bg-orange-500', Icon: Medal },
};

const Podium = ({ e, tall, settings, open, onToggle }) => {
  const m = MEDAL[e.medal];
  const Icon = m.Icon;
  const order = { gold: 'order-1 sm:order-2', silver: 'order-2 sm:order-1', bronze: 'order-3' }[e.medal];
  return (
    <div data-testid={`leaderboard-podium-${e.medal}`}
      className={`relative flex-1 rounded-2xl border-2 bg-gradient-to-b p-3 sm:p-4 shadow-lg ${m.card} ${order} ${tall ? 'sm:-mt-4 sm:pb-6' : ''}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`inline-flex items-center gap-1 text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] ${m.text}`}>
          <Icon className="w-4 h-4" /> {m.label}
        </span>
        <span className={`text-[10px] font-extrabold uppercase tracking-wider ${m.text}`}>{m.place}</span>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <span className={`h-11 w-11 sm:h-12 sm:w-12 rounded-full flex items-center justify-center text-sm font-black ring-4 shrink-0 ${m.ring}`}>{initials(e.name)}</span>
        <div className="min-w-0">
          <p className="text-sm sm:text-base font-extrabold text-slate-900 leading-tight truncate" data-testid={`leaderboard-${e.medal}-name`}>{e.name}</p>
          <p className="text-[11px] text-slate-600 font-medium">{e.jobs} job{e.jobs === 1 ? '' : 's'} · {e.invoiced_rows} invoice{e.invoiced_rows === 1 ? '' : 's'}</p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-2">
        <p className={`font-mono font-black tracking-tight text-slate-900 ${tall ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'}`} data-testid={`leaderboard-${e.medal}-total`}>{money(e.total)}</p>
        <MarginPill e={e} settings={settings} open={open} onToggle={onToggle} />
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-white/70 overflow-hidden">
        <div className={`h-full rounded-full ${m.bar}`} style={{ width: `${Math.max(4, e.share * 100)}%` }} />
      </div>
      {open && <MarginDetail e={e} />}
    </div>
  );
};

const Row = ({ e, settings, open, onToggle }) => (
  <div className="rounded-xl bg-white/80 border border-slate-200 px-3 py-2" data-testid={`leaderboard-row-${e.rank}`}>
    <div className="flex items-center gap-3">
      <span className="w-6 text-center text-xs font-black text-slate-500">#{e.rank}</span>
      <span className="h-8 w-8 rounded-full bg-slate-200 text-slate-700 text-xs font-black flex items-center justify-center">{initials(e.name)}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-bold text-slate-900 truncate">{e.name}</span>
        <span className="block text-[11px] text-slate-500">{e.jobs} jobs · {e.invoiced_rows} invoices</span>
      </span>
      <MarginPill e={e} settings={settings} open={open} onToggle={onToggle} small />
      <span className="font-mono font-bold text-slate-900 text-sm">{money(e.total)}</span>
    </div>
    {open && <MarginDetail e={e} />}
  </div>
);

const CHIP = {
  gold: 'bg-amber-100 border-amber-400 text-amber-950',
  silver: 'bg-slate-200 border-slate-400 text-slate-900',
  bronze: 'bg-orange-100 border-orange-400 text-orange-950',
};

// Small strip under the podium: how the race finished last fiscal year (includes PMs who have since left).
const LastYearStrip = ({ block }) => (
  <div className="mt-4 rounded-2xl border border-slate-200 bg-white/70 px-3 py-2.5" data-testid="leaderboard-last-year">
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 inline-flex items-center gap-1"><History className="w-3 h-3" /> Last year · FY{block.long}</span>
      <span className="text-[10px] font-semibold text-slate-400">team {compact(block.total)}</span>
    </div>
    <div className="mt-2 flex flex-wrap gap-1.5">
      {block.entries.map(e => (
        <span key={e.pm} data-testid={`leaderboard-last-year-${e.rank}`} title={`${e.name} · ${money(e.total)} · ${e.jobs} jobs`}
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${e.medal ? CHIP[e.medal] : 'bg-white border-slate-200 text-slate-700'} ${e.active ? '' : 'opacity-60'}`}>
          {e.medal ? <Medal className="w-3.5 h-3.5" /> : <span className="text-[10px] font-black text-slate-400">#{e.rank}</span>}
          <span className="font-bold">{e.active ? e.name : e.pm}</span>
          <span className="font-mono font-semibold">{compact(e.total)}</span>
          {e.margin?.pct != null && <span className="font-mono text-[10px] opacity-80" data-testid={`leaderboard-last-year-margin-${e.rank}`}>· {pct(e.margin.pct)} margin</span>}
        </span>
      ))}
    </div>
  </div>
);

// Prominent PM / estimator leaderboard: YTD total sales (col K of each PM's Work Orders tab), gold / silver / bronze on top.
export const SalesLeaderboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [openPm, setOpenPm] = useState(null);
  const allowed = ROLES.includes(user?.role);
  useEffect(() => {
    if (!allowed) return undefined;
    let alive = true;
    const load = () => axios.get(`${API}/sales-leaderboard`).then(r => { if (alive) setData(r.data); }).catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [allowed]);
  const years = data?.years || [];
  const block = years.find(y => y.is_current) || years[0];
  const last = years.find(y => y.label === 'Last year' && y.entries?.length);
  if (!allowed || !block) return null;
  const medals = block.entries.filter(e => e.medal);
  const rest = block.entries.filter(e => !e.medal);
  const settings = data?.margin_settings;
  const toggle = (pm) => setOpenPm(p => (p === pm ? null : pm));
  const anyMargin = block.entries.some(e => e.margin);
  const updated = block.updated_at ? new Date(block.updated_at) : null;
  const ago = updated ? Math.max(0, Math.round((Date.now() - updated.getTime()) / 60000)) : null;
  return (
    <section data-testid="sales-leaderboard" className="rounded-3xl border border-slate-200/90 bg-[#F8FAFD]/95 backdrop-blur-md shadow-[0_2px_12px_-2px_rgba(15,23,42,0.08)] p-4 sm:p-5">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-10 w-10 rounded-2xl bg-slate-900 text-amber-300 flex items-center justify-center shrink-0"><Trophy className="w-5 h-5" /></span>
          <div className="min-w-0">
            <h2 className="text-lg md:text-xl font-extrabold text-slate-900 tracking-tight leading-tight" data-testid="sales-leaderboard-title">Sales Leaderboard</h2>
            <p className="text-[11px] text-slate-500 font-medium">FY{block.long} · {block.label.toLowerCase()} · YTD total sales from the Work Orders book{ago !== null ? ` · updated ${ago === 0 ? 'just now' : `${ago} min ago`}` : ''}</p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2.5 py-1 text-xs font-bold" data-testid="leaderboard-company-total">
            <TrendingUp className="w-3.5 h-3.5" /> Team {compact(block.total)}
          </span>
        </div>
      </div>

      {medals.length > 0 ? (
        <div className="mt-5 sm:mt-7 flex flex-col sm:flex-row sm:items-start gap-3">
          {medals.map(e => <Podium key={e.pm} e={e} tall={e.medal === 'gold'} settings={settings} open={openPm === e.pm} onToggle={() => toggle(e.pm)} />)}
        </div>
      ) : (
        <p className="mt-4 text-sm text-slate-500 flex items-center gap-2" data-testid="leaderboard-empty"><RefreshCw className="w-4 h-4" /> No sales booked in FY{block.long} yet — the podium fills in as column K totals land.</p>
      )}

      {rest.length > 0 && <div className="mt-3 space-y-1.5">{rest.map(e => <Row key={e.pm} e={e} settings={settings} open={openPm === e.pm} onToggle={() => toggle(e.pm)} />)}</div>}
      {anyMargin && (
        <p className="mt-2 text-[10px] text-slate-400" data-testid="leaderboard-margin-note">
          Margin = Ops estimate: invoiced Sub-Total less crew hours at pay rate + ${settings?.burden_per_hour ?? 29}/h burden, POs and materials on the WO · $-weighted over invoiced jobs with hours logged{data?.margin_scope === 'own' ? ' · you see your own only' : ''}
        </p>
      )}
      {last && <LastYearStrip block={last} />}
    </section>
  );
};
